import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Snackbar,
} from '@mui/material';
import { ArrowBack, Add, Refresh, FactCheckOutlined } from '@mui/icons-material';
import { PageHeader, StatCard, TableSurface, headerCellSx, bodyRowSx, TableEmptyState, ErrorState } from '../../components/common/pageKit';
import { assetService } from '../../services/assetService';
import { fmtDate } from './assetFormat';

const FOUND_STATUSES = ['FOUND', 'MISSING', 'MOVED', 'DAMAGED', 'NOT_TAGGED'];
const STATUS_COLOR: Record<string, any> = { FOUND: 'success', MISSING: 'error', MOVED: 'warning', DAMAGED: 'warning', NOT_TAGGED: 'default' };
const todayISO = () => new Date().toISOString().slice(0, 10);

const PhysicalVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: '', campaignDate: todayISO(), location: '' });
  const [selected, setSelected] = useState<any | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [assets, setAssets] = useState<any[]>([]);
  const [rows, setRows] = useState<Record<number, { foundStatus: string; remarks: string; checked: boolean }>>({});

  const load = useCallback(() => {
    setLoading(true);
    assetService.listVerificationCampaigns()
      .then((r) => { setCampaigns(r.campaigns || []); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load campaigns'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createCampaign = async () => {
    if (!form.name.trim()) return;
    try {
      const { campaign } = await assetService.createVerificationCampaign(form);
      setToast('Campaign created'); setNewOpen(false); setForm({ name: '', campaignDate: todayISO(), location: '' });
      load(); openCampaign(campaign);
    } catch (e: any) { setToast(e.message || 'Failed'); }
  };

  const openCampaign = async (c: any) => {
    setSelected(c);
    try {
      const [summary, list] = await Promise.all([
        assetService.getVerificationSummary(c.id),
        assetService.getAssets({ limit: 200 } as any),
      ]);
      setDetail(summary);
      const as = (list as any).assets || [];
      setAssets(as);
      const seed: Record<number, any> = {};
      as.forEach((a: any) => { seed[a.id] = { foundStatus: 'FOUND', remarks: '', checked: false }; });
      setRows(seed);
    } catch (e: any) { setToast(e.message || 'Failed to open'); }
  };

  const saveCounts = async () => {
    if (!selected) return;
    const counts = Object.entries(rows).filter(([, v]) => v.checked)
      .map(([assetId, v]) => ({ assetId: Number(assetId), foundStatus: v.foundStatus, remarks: v.remarks }));
    if (!counts.length) { setToast('Tick the assets you verified'); return; }
    try {
      const summary = await assetService.recordVerificationCounts(selected.id, counts);
      setDetail(summary); setToast(`Recorded ${counts.length} counts`); load();
    } catch (e: any) { setToast(e.message || 'Failed'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/assets')}>Assets</Button>
      </Stack>
      <PageHeader title="Physical Verification" subtitle="Run verification campaigns and reconcile expected vs found assets."
        actions={<><Button variant="outlined" startIcon={<Refresh />} onClick={load} sx={{ mr: 1 }}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setNewOpen(true)}>New Campaign</Button></>} />

      {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={load} /></Box>}

      <TableSurface>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>{['Campaign', 'Date', 'Location', 'Counted', 'Status', ''].map((h) => <TableCell key={h} sx={headerCellSx}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
              ) : !campaigns.length ? (
                <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="No campaigns yet" hint="Create a verification campaign to start counting assets." /></TableCell></TableRow>
              ) : campaigns.map((c) => (
                <TableRow key={c.id} hover sx={{ ...(bodyRowSx as any), cursor: 'pointer' }} onClick={() => openCampaign(c)}>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{c.name}</Typography></TableCell>
                  <TableCell>{fmtDate(c.campaign_date)}</TableCell>
                  <TableCell>{c.location || '—'}</TableCell>
                  <TableCell>{c.counted || 0}</TableCell>
                  <TableCell><Chip size="small" label={c.status} /></TableCell>
                  <TableCell><Button size="small" onClick={(e) => { e.stopPropagation(); openCampaign(c); }}>Open</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {/* New campaign */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>New Verification Campaign</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
            <TextField size="small" type="date" label="Campaign date" InputLabelProps={{ shrink: true }} value={form.campaignDate} onChange={(e) => setForm({ ...form, campaignDate: e.target.value })} />
            <TextField size="small" label="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setNewOpen(false)}>Cancel</Button><Button variant="contained" onClick={createCampaign}>Create</Button></DialogActions>
      </Dialog>

      {/* Campaign detail / counting */}
      <Dialog open={!!selected} onClose={() => { setSelected(null); setDetail(null); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}><FactCheckOutlined /> {selected?.name}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {FOUND_STATUSES.map((s) => (
                <Grid size={{ xs: 6, sm: 2.4 }} key={s}><StatCard title={s.replace('_', ' ')} value={String(detail.summary?.[s] ?? 0)} icon={<FactCheckOutlined />} color={s === 'FOUND' ? '#059669' : s === 'MISSING' ? '#dc2626' : '#d97706'} /></Grid>
              ))}
            </Grid>
          )}
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Record counts</Typography>
          <TableContainer sx={{ maxHeight: 340, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>{['✓', 'Asset', 'Found status', 'Remarks'].map((h) => <TableCell key={h} sx={headerCellSx}>{h}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {assets.map((a) => {
                  const r = rows[a.id] || { foundStatus: 'FOUND', remarks: '', checked: false };
                  return (
                    <TableRow key={a.id} hover>
                      <TableCell padding="checkbox"><input type="checkbox" checked={r.checked} onChange={(e) => setRows({ ...rows, [a.id]: { ...r, checked: e.target.checked } })} /></TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{a.asset_code}</Typography><Typography variant="caption" color="text.secondary">{a.name}</Typography></TableCell>
                      <TableCell>
                        <TextField select size="small" value={r.foundStatus} onChange={(e) => setRows({ ...rows, [a.id]: { ...r, foundStatus: e.target.value, checked: true } })} sx={{ minWidth: 130 }}>
                          {FOUND_STATUSES.map((s) => <MenuItem key={s} value={s}><Chip size="small" label={s.replace('_', ' ')} color={STATUS_COLOR[s]} sx={{ height: 20 }} /></MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell><TextField size="small" value={r.remarks} onChange={(e) => setRows({ ...rows, [a.id]: { ...r, remarks: e.target.value } })} placeholder="—" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSelected(null); setDetail(null); }}>Close</Button>
          <Button variant="contained" onClick={saveCounts}>Record Counts</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default PhysicalVerificationPage;
